import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Assessment,
  Business,
  AttachMoney,
  ShowChart,
  Timeline,
  CompareArrows
} from '@mui/icons-material';
import { ExtendedMarketResearch } from '../types';
import { alpha } from '@mui/material/styles';

interface Props {
  data: ExtendedMarketResearch;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: 'up' | 'down' | 'neutral';
  icon: React.ReactNode;
  color: string;
  progress?: number;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  trend,
  icon,
  color,
  progress
}) => {
  const theme = useTheme();

  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <TrendingUp sx={{ fontSize: 14, color: '#4caf50' }} />;
      case 'down':
        return <TrendingDown sx={{ fontSize: 14, color: '#f44336' }} />;
      default:
        return null;
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: '120px',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        },
        '@media print': {
          boxShadow: 'none',
          border: '1px solid #e0e0e0'
        }
      }}
    >
      <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Box
            sx={{
              p: 0.75,
              borderRadius: 1,
              backgroundColor: `${color}15`,
              color: color,
              mr: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& svg': {
                fontSize: 20
              }
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 600,
                fontSize: '0.7rem',
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {title}
            </Typography>
          </Box>
          {getTrendIcon()}
        </Box>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 0.5,
            lineHeight: 1.2,
            fontSize: {
              xs: '0.95rem',
              sm: '1rem',
              md: '0.9rem',
              lg: '0.95rem'
            },
            wordBreak: 'break-word',
            minHeight: '1.3em',
            flex: '0 0 auto'
          }}
        >
          {value}
        </Typography>

        {subtitle && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              fontSize: '0.7rem',
              mb: progress !== undefined ? 1 : 0,
              lineHeight: 1.3,
              flex: '0 0 auto'
            }}
          >
            {subtitle}
          </Typography>
        )}

        {progress !== undefined && (
          <Box sx={{ mt: 'auto', pt: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 3,
                borderRadius: 2,
                backgroundColor: 'rgba(0,0,0,0.08)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: color,
                  borderRadius: 2
                }
              }}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export const ProfessionalMetrics: React.FC<Props> = ({ data }) => {
  const theme = useTheme();
  const research = data.research_data;

  // Extract key metrics from the research data
  const extractMetrics = () => {
    const marketOverview: any = research?.marketOverview;
    const competitive = research?.competitiveAnalysis;
    const supply = research?.supplyAnalysis;
    const economic = research?.economicIndicators;

    const formatCurrency = (value: number) => {
      if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
      if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
      if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
      return `$${value}`;
    };

    const marketSizeVal = (marketOverview as any)?.marketSize;
    const growthRateVal = (marketOverview as any)?.growthRate;

    return {
      marketSize: typeof marketSizeVal === 'number'
        ? formatCurrency(marketSizeVal)
        : marketOverview?.summary?.includes('$')
          ? marketOverview.summary.match(/\$[\d,]+[MB]/)?.[0] || 'N/A'
          : 'N/A',
      growthRate: typeof growthRateVal === 'number'
        ? `${growthRateVal.toFixed(1)}%`
        : marketOverview?.summary?.includes('%')
          ? marketOverview.summary.match(/\d+\.?\d*%/)?.[0] || 'N/A'
          : 'N/A',
      competitivePosition: competitive?.marketPenetration || 'Analyzing...',
      supplyGrowth: supply?.supplyGrowth || 'Stable',
      demandDrivers: marketOverview?.demandDrivers?.length || 0,
      employmentTrend: (economic?.employmentTrends?.includes('positive') ? 'up' :
                       economic?.employmentTrends?.includes('negative') ? 'down' : 'neutral') as 'up' | 'down' | 'neutral'
    };
  };

  const metrics = extractMetrics();

  const metricData: MetricCardProps[] = [
    {
      title: 'MARKET',
      value: metrics.marketSize,
      subtitle: 'Total addressable market',
      trend: 'up' as const,
      icon: <Business />,
      color: theme.palette.primary.main,
      progress: 75
    },
    {
      title: 'GROWTH',
      value: metrics.growthRate,
      subtitle: 'Year-over-year growth',
      trend: metrics.growthRate !== 'N/A' ? 'up' as const : 'neutral' as const,
      icon: <ShowChart />,
      color: theme.palette.success.main,
      progress: 82
    },
    {
      title: 'POSITION',
      value: metrics.competitivePosition.length > 15
        ? 'Strong'
        : metrics.competitivePosition || 'Moderate',
      subtitle: 'Market penetration',
      trend: 'neutral' as const,
      icon: <CompareArrows />,
      color: theme.palette.warning.main,
      progress: 65
    },
    {
      title: 'DEMAND',
      value: metrics.demandDrivers,
      subtitle: 'Key market factors',
      trend: metrics.demandDrivers > 3 ? 'up' as const : 'neutral' as const,
      icon: <Assessment />,
      color: theme.palette.secondary.main,
      progress: Math.min(metrics.demandDrivers * 20, 100)
    },
    {
      title: 'SUPPLY',
      value: metrics.supplyGrowth.length > 15
        ? metrics.supplyGrowth.split(' ')[0]
        : metrics.supplyGrowth,
      subtitle: 'Market supply trends',
      trend: metrics.supplyGrowth.toLowerCase().includes('growth') ? 'up' as const : 'neutral' as const,
      icon: <Timeline />,
      color: theme.palette.error.main,
      progress: 58
    },
    {
      title: 'OUTLOOK',
      value: metrics.employmentTrend === 'up' ? 'Positive' :
             metrics.employmentTrend === 'down' ? 'Negative' : 'Stable',
      subtitle: 'Employment & tourism',
      trend: metrics.employmentTrend,
      icon: <AttachMoney />,
      color: theme.palette.info.main,
      progress: metrics.employmentTrend === 'up' ? 85 :
                metrics.employmentTrend === 'down' ? 35 : 60
    }
  ];

  return (
    <Box sx={{ mb: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '1.1rem'
          }}
        >
          <Assessment sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          Key Performance Indicators
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.75rem' }}
        >
          Critical metrics for investment analysis and market positioning assessment
        </Typography>
      </Box>

      {/* Metrics Grid */}
      <Grid container spacing={1.5}>
        {metricData.map((metric, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} xl={2} key={index}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>

      {/* Market Status Summary */}
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          '@media print': {
            backgroundColor: '#f8f9fa',
            border: '1px solid #e0e0e0'
          }
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Market Assessment:
              </Typography>
              <Chip
                label={metrics.demandDrivers > 3 ? "Strong Opportunity" : "Moderate Opportunity"}
                color={metrics.demandDrivers > 3 ? "success" : "warning"}
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}
              />
              <Typography variant="body2" color="text.secondary">
                with {metrics.demandDrivers} key demand drivers identified
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.7rem' }}
              >
                Last Updated: {data.createdAt ? new Date(data.createdAt).toLocaleDateString() : 'Today'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ProfessionalMetrics;